import React from 'react';

import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {
  ActionOrigin,
  ProcessTaskStatusType,
  ProcessExecutionType,
  ProcessClientOrigin,
  ProcessPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ItemListingQuery} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {statusTypeToDisplayName} from 'processes/ProcessConstants';
import {isProcessPlatformFeatureEnabledAndHasCapability} from 'processes/processes-platform/FeatureOptions';
import {ExecutableProcessTask} from 'processes/util/ProcessTaskExecutionUtil';

import ExecuteButton from './ExecuteButton';
import {
  SidepanelProcessItem,
  sidepanelProcessItemIsProcessTask,
  doSelectedItemsExceedLimit,
} from './ProcessManagementSidepanelConstants';

type SidepanelProcessTask = ExtractByTypename<ItemListingQuery['processTasks'][number], 'ProcessTaskNode'>;
type ProcessTaskSchema = ExtractByTypename<SidepanelProcessTask['schema'], 'ProcessTaskSchemaNode'>;
type EntryTemplate = ExtractByTypename<ProcessTaskSchema['entryTemplate'], 'TemplateNode'>;
interface ExecuteTasksButtonProps {
  selectedItems: Array<SidepanelProcessItem>;
  actionOrigin?: ActionOrigin;
  isSidePanel?: boolean;
}

interface MaybeExecutableSchema {
  id: string;
  name: string;
  executionType: ProcessExecutionType;
  clientOrigin: ProcessClientOrigin;
  entryTemplate: EntryTemplate | null;
  outputSchema: unknown;
}

export function isDirectExecutionTaskWithNoOutputSchema(schema: MaybeExecutableSchema): boolean {
  return schema.executionType === ProcessExecutionType.DIRECT && schema.outputSchema == null;
}

export function getExecuteMessageForTasksWithSchema(
  schema: MaybeExecutableSchema,
  numTasks: number,
  isSidePanel: boolean = false
): string | null {
  if (
    schema.executionType === ProcessExecutionType.ENTRY &&
    numTasks > BenchlingConfig.WORKFLOWS_MAX_TASKS_PER_ENTRY
  ) {
    return `You cannot execute more than ${BenchlingConfig.WORKFLOWS_MAX_TASKS_PER_ENTRY} tasks into a single entry`;
  } else if (
    schema.executionType === ProcessExecutionType.ENTRY &&
    BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING &&
    schema.entryTemplate?.currentPublishingRecord == null
  ) {
    return `Task execution is disabled because the execution template is unavailable. Contact your admin for assistance.`;
  } else if (
    schema.executionType === ProcessExecutionType.DIRECT &&
    numTasks > BenchlingConfig.WORKFLOWS_MAX_DIRECT_EXECUTION_TASKS
  ) {
    return `Cannot execute more than ${BenchlingConfig.WORKFLOWS_MAX_DIRECT_EXECUTION_TASKS} tasks at a time`;
  } else if (isDirectExecutionTaskWithNoOutputSchema(schema) && schema.entryTemplate == null) {
    if (isSidePanel) {
      return 'To execute these tasks, edit their details and statuses in their details pages, or click "Edit" in the toolbar to the left';
    }
    return 'You cannot execute direct execution tasks without an output schema configuration';
  } else if (
    !isProcessPlatformFeatureEnabledAndHasCapability(
      ProcessPlatformFeatureType.IS_DIRECTLY_EXECUTABLE,
      schema.executionType,
      schema.clientOrigin,
      schema.name
    )
  ) {
    return 'All selected tasks must be directly executable';
  }

  return null;
}

export function getProcessItemExecuteTooltipMessage(
  selectedItems: Array<SidepanelProcessItem>,
  isSidePanel: boolean = false
): string | null {
  if (selectedItems.length === 0) {
    return 'No tasks selected';
  } else if (!selectedItems.every((item) => sidepanelProcessItemIsProcessTask(item))) {
    return 'All selected items must be tasks';
  }

  const selectedTasks = selectedItems as Array<SidepanelProcessTask>;

  return getExecuteTooltipMessage(selectedTasks, isSidePanel);
}

function getExecuteTooltipMessageForProcedureTasks(
  selectedTasks: Array<ExecutableProcessTask>,
  clientOrigin: ProcessClientOrigin.ASSAYS | ProcessClientOrigin.RECIPES
): string | null {
  if (selectedTasks.some((task) => task.schema.executionType === ProcessExecutionType.PROCEDURE)) {
    return `You cannot execute ${processExecutionTypeDisplayName(
      clientOrigin,
      ProcessExecutionType.PROCEDURE
    )}s`;
  }
  if (selectedTasks.every((task) => task.schema.executionType === ProcessExecutionType.PROCEDURE_METHOD)) {
    const worksheets = selectedTasks.map((task) =>
      task.procedureMethodExecutionInstance && !isInaccessible(task.procedureMethodExecutionInstance)
        ? task.procedureMethodExecutionInstance.worksheet
        : undefined
    );
    if (worksheets.some((worksheet) => worksheet === undefined)) {
      return 'Cannot open worksheets for some tasks';
    }
    if (!worksheets.every((worksheet) => worksheet !== undefined && worksheet === worksheets[0])) {
      return 'All tasks must be part of the same worksheet';
    }
  }
  if (selectedTasks.every((task) => task.schema.executionType === ProcessExecutionType.PROCEDURE_STEP)) {
    const documents = selectedTasks.map((task) =>
      task.procedureStepExecutionInstance && !isInaccessible(task.procedureStepExecutionInstance)
        ? task.procedureStepExecutionInstance?.document
        : undefined
    );
    if (documents.some((document) => document === undefined)) {
      return 'Cannot open steps for some tasks';
    }
    if (!documents.every((document) => document !== undefined && document === documents[0])) {
      return 'All tasks must be part of the same step';
    }
  }

  return null;
}

export function getExecuteTooltipMessage(
  selectedTasks: Array<ExecutableProcessTask>,
  isSidePanel: boolean = false
): string | null {
  const selectedOrigins = new Set(selectedTasks.map((task) => task.schema.clientOrigin));

  if (selectedTasks.some((task) => isArchived(task))) {
    return `Unarchive ${selectedTasks.length > 1 ? 'all tasks' : 'this task'} to execute ${
      selectedTasks.length > 1 ? 'them' : 'it'
    }`;
  }

  // As of this writing, tasks of different origins will always have different schemas,
  // but these are technically different things so future proofing by checking both
  if (
    selectedOrigins.size > 1 ||
    !selectedTasks.every((task) => task.schema.id === selectedTasks[0].schema.id)
  ) {
    return 'All selected tasks must have the same schema';
  }

  const [selectedOrigin] = selectedOrigins;

  // From the perspective of this execute button, "executing" a procedure task just opens the worksheet
  // so we don't need to check the platform capabilities. That's for actually executing tasks on the backend
  if (selectedOrigin === ProcessClientOrigin.RECIPES || selectedOrigin === ProcessClientOrigin.ASSAYS) {
    return getExecuteTooltipMessageForProcedureTasks(selectedTasks, selectedOrigin);
  } else if (selectedOrigin === ProcessClientOrigin.PROCESSES) {
    if (!selectedTasks.every((task) => task.status.statusType === ProcessTaskStatusType.PENDING)) {
      return `${
        selectedTasks.length > 1 ? 'All selected tasks' : 'Selected task'
      } must be ${statusTypeToDisplayName(ProcessTaskStatusType.PENDING)}`;
    } else {
      const schemaValidationMessage = getExecuteMessageForTasksWithSchema(
        selectedTasks[0].schema,
        selectedTasks.length,
        isSidePanel
      );
      if (schemaValidationMessage != null) {
        return schemaValidationMessage;
      }
    }
  } else {
    // This shouldn't be possible.
    return `Unsupported task(s)`;
  }

  return null;
}

export function isProcessTaskExecutionAllowed(selectedItems: Array<SidepanelProcessItem>): boolean {
  return getProcessItemExecuteTooltipMessage(selectedItems) == null;
}

export default function ExecuteTasksButton({
  selectedItems,
  actionOrigin,
  isSidePanel,
}: ExecuteTasksButtonProps): JSX.Element {
  const disabledTooltip = getProcessItemExecuteTooltipMessage(selectedItems, isSidePanel ?? false);
  const {isExceedingLimitMessage} = doSelectedItemsExceedLimit(selectedItems);

  return (
    <ExecuteButton
      getTasksToExecute={() => Promise.resolve(selectedItems as Array<ExecutableProcessTask>)}
      disabledMessage={disabledTooltip || isExceedingLimitMessage}
      actionOrigin={actionOrigin}
    />
  );
}
